import {
  ArchiveIcon,
  ArrowsClockwiseIcon,
  BasketIcon,
  BellIcon,
  BellSlashIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CheckIcon,
  DeviceMobileIcon,
  HouseIcon,
  PaletteIcon,
  ChartLineUpIcon,
  PlusIcon,
  RepeatIcon,
  SignOutIcon,
  SpinnerGapIcon,
  TrashIcon,
  UsersThreeIcon,
  WalletIcon,
} from '@phosphor-icons/react'
import { MAX_PERIOD_ANCHOR_DAY, type PlainDate, formatMoney, toParts } from '@paybae/shared'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { ListaHogares } from '@/components/CambiarHogar'
import { Pastilla } from '@/components/Editorial'
import { Donut, type DonutSegment, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Interruptor,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { Invitacion } from '@/components/Invitacion'
import { PersonAvatar } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import {
  type Account,
  type AccountType,
  ApiError,
  type Member,
  type MemberRole,
  type NonBusinessDayRule,
  type PayFrequency,
  type IncomeKind,
  type IncomeSource,
  type Treatment,
  nombreDe,
} from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { PERSON_TOKENS, personPalette } from '@/lib/persons'
import { type EstadoPush, activarPush, estadoPush } from '@/lib/push'
import {
  useAccounts,
  useActualizarCuenta,
  useActualizarHogar,
  useActualizarIntegrante,
  useActualizarMiPerfil,
  useArchivarCuenta,
  useConciliarCuenta,
  useCrearCuenta,
  useBorrarCuenta,
  useBorrarIngreso,
  useGuardarIngreso,
  useHousehold,
  useIngresos,
  useRecurring,
} from '@/lib/queries'
import { useSession } from '@/lib/session'
import {
  PALETAS,
  type PaletaId,
  type ThemePreference,
  applyPaleta,
  applyTheme,
  getPaleta,
  getStoredPreference,
} from '@/lib/theme'

/**
 * Ajustes.
 *
 * Es la pantalla donde se cambia todo lo que la app deduce sola el resto del
 * tiempo: cómo te pagan, dónde tienes el dinero, quién es quién en el hogar.
 * Va por secciones y no como una lista larga porque cada bloque contesta una
 * pregunta distinta, y encontrar «mi nómina» entre veinte filas sueltas cuesta
 * más que abrirla.
 *
 * Cada bloque abre su propia hoja de formulario en vez de editar en línea: los
 * cambios de dinero se confirman con un botón, nunca al soltar el dedo.
 */

const TIPO_HOGAR: Record<string, string> = {
  PERSONAL: 'Personal',
  COUPLE: 'Pareja',
  GROUP: 'Familia',
}

const ROL: Record<MemberRole, string> = {
  ADMIN: 'Administra',
  MEMBER: 'Integrante',
  LIMITED: 'Acceso limitado',
}

const TIPO_CUENTA: Record<AccountType, string> = {
  CHECKING: 'Débito',
  CASH: 'Efectivo',
  SAVINGS: 'Ahorro',
  MEAL_VOUCHER: 'Vales',
  INVESTMENT: 'Inversión',
}

const FRECUENCIA: Record<PayFrequency, string> = {
  SEMI_MONTHLY: 'Quincenal',
  MONTHLY: 'Mensual',
  BIWEEKLY: 'Catorcenal',
  WEEKLY: 'Semanal',
  IRREGULAR: 'Sin calendario',
}

/** Marcador para «sin dueño» en el selector: el fondo común guarda `null`. */
const DUENO_COMUN = '__comun__'

/** Qué hoja de formulario está abierta encima de la pantalla. */
type Hoja =
  | { tipo: 'perfil'; miembro: Member }
  | { tipo: 'ingreso'; fuente: IncomeSource | null }
  | { tipo: 'aportacion' }
  | { tipo: 'hogar' }
  | { tipo: 'cuenta-nueva' }
  | { tipo: 'cuenta'; cuenta: Account }
  | { tipo: 'integrante'; miembro: Member }
  | null

export function Settings() {
  const { profile, membership, signOut } = useSession()
  const hogar = useHousehold()
  const cuentas = useAccounts()
  const ingresos = useIngresos(membership.id)
  const [hoja, setHoja] = useState<Hoja>(null)
  // Ajustes es larga. Quien llega desde el atajo de «Invitar» del inicio tiene
  // que aterrizar en la sección, no en lo alto de la pantalla a buscarla.
  const [params] = useSearchParams()
  const verSeccion = params.get('ver')

  if (hogar.isPending) {
    return (
      <>
        <ScreenHeader title="Ajustes" />
        <div className="px-safe mx-auto flex max-w-lg flex-col gap-3 pt-1">
          <Skeleton className="h-32" />
          <Skeleton className="h-48" />
        </div>
      </>
    )
  }

  if (hogar.isError || !hogar.data) {
    return (
      <>
        <ScreenHeader title="Ajustes" />
        <ErrorState
          titulo="No pudimos cargar tu hogar"
          mensaje={hogar.error instanceof Error ? hogar.error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void hogar.refetch()}
        />
      </>
    )
  }

  const data = hogar.data
  const esAdmin = membership.role === 'ADMIN'
  const esPersonal = data.kind === 'PERSONAL'
  const yo = data.members.find((m) => m.id === membership.id)

  const cerrar = (): void => setHoja(null)

  return (
    <>
      <ScreenHeader title="Ajustes" subtitle={`${data.name} · ${TIPO_HOGAR[data.kind] ?? data.kind}`} />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-5 pt-1 pb-2">
        {yo ? (
          <section>
            <SectionHeader title="Tu perfil" />
            <MiPerfil miembro={yo} onEditar={() => setHoja({ tipo: 'perfil', miembro: yo })} />
          </section>
        ) : null}

        <CambiarHogar />

        <AvisosPush />

        <Apariencia />

        <section>
          <SectionHeader title="Tus ingresos" />
          {ingresos.isPending ? (
            <Skeleton className="h-24" />
          ) : (
            <ListaIngresos
              fuentes={ingresos.data ?? []}
              onEditar={(fuente) => setHoja({ tipo: 'ingreso', fuente })}
              onCrear={() => setHoja({ tipo: 'ingreso', fuente: null })}
            />
          )}
        </section>

        {!esPersonal ? (
          <section>
            <SectionHeader title="Tu aportación al fondo común" />
            <Card className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
                  <UsersThreeIcon size={20} weight="fill" />
                </span>
                <div className="min-w-0 flex-1">
                  <p data-money className="font-display text-[20px] font-extrabold tracking-[-0.03em] text-ink">
                    {formatMoney(yo?.fixedContributionCents ?? 0, { hideZeroCents: true })}
                  </p>
                  <p className="text-[12px] leading-snug text-ink-soft">
                    Al mes. Se resta de tu dinero libre y se suma al del hogar.
                  </p>
                </div>
              </div>
              {esAdmin ? (
                <BotonSecundario onClick={() => setHoja({ tipo: 'aportacion' })}>
                  Cambiar mi aportación
                </BotonSecundario>
              ) : (
                <p className="text-[12px] leading-snug text-ink-faint">
                  Solo quien administra el hogar puede cambiar las aportaciones. Pídeselo y
                  aquí verás el nuevo monto.
                </p>
              )}
            </Card>
          </section>
        ) : null}

        <section>
          <SectionHeader
            title="Cuentas"
            action={
              <button
                type="button"
                onClick={() => {
                  haptic('light')
                  setHoja({ tipo: 'cuenta-nueva' })
                }}
                className="-my-2.5 -mr-2 flex min-h-[44px] shrink-0 items-center gap-1 rounded-card px-2 text-[13px] font-semibold text-primary active:scale-95"
              >
                <PlusIcon size={14} weight="bold" />
                Agregar
              </button>
            }
          />
          {cuentas.isPending ? (
            <Skeleton className="h-40" />
          ) : (
            <ListaCuentas
              cuentas={cuentas.data ?? []}
              onAbrir={(cuenta) => setHoja({ tipo: 'cuenta', cuenta })}
              onCrear={() => setHoja({ tipo: 'cuenta-nueva' })}
            />
          )}
        </section>

        <section>
          <SectionHeader title="Gastos fijos" />
          <EnlaceGastosFijos />
        </section>

        <section>
          <SectionHeader title="Preferencias del hogar" />
          <Card className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
                <HouseIcon size={20} weight="fill" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-ink">{data.name}</p>
                <p className="text-[12px] leading-snug text-ink-soft">
                  {data.countExpectedIncome
                    ? 'Te anticipamos lo que falta por caer, aparte del disponible.'
                    : 'Solo verás el dinero que ya está en tus cuentas.'}
                </p>
              </div>
            </div>
            {esAdmin ? (
              <BotonSecundario onClick={() => setHoja({ tipo: 'hogar' })}>
                Editar preferencias
              </BotonSecundario>
            ) : null}
          </Card>
        </section>

        <section>
          <SectionHeader title={`Integrantes (${data.members.length})`} />
          <Card className="divide-y divide-line p-0">
            {data.members.map((m) => {
              const fila = (
                <>
                  {/* El color de identidad sale de `personColorToken`, nunca del
                      id: los dos son cadenas y el compilador no lo notaría, pero
                      todos acabarían del mismo color. */}
                  <PersonAvatar name={m.user.displayName} token={m.personColorToken} size="md" />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-[14px] font-medium text-ink">
                      {m.user.displayName}
                      {m.user.id === profile.id ? (
                        <span className="ml-1.5 text-[12px] font-normal text-ink-faint">(tú)</span>
                      ) : null}
                    </p>
                    <p className="truncate text-[12px] text-ink-soft">
                      {ROL[m.role] ?? m.role}
                      {m.fixedContributionCents > 0
                        ? ` · aporta ${formatMoney(m.fixedContributionCents, { hideZeroCents: true })} al mes`
                        : ''}
                    </p>
                  </div>
                </>
              )

              return esAdmin ? (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setHoja({ tipo: 'integrante', miembro: m })
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left active:scale-[0.99]"
                >
                  {fila}
                  <CaretRightIcon size={16} className="shrink-0 text-ink-faint" />
                </button>
              ) : (
                <div key={m.id} className="flex items-center gap-3 px-4 py-3">
                  {fila}
                </div>
              )
            })}
          </Card>
          {esAdmin ? (
            <p className="mt-2 px-1 text-[12px] leading-snug text-ink-faint">
              Toca a alguien para cambiar su rol o su color.
            </p>
          ) : null}
        </section>

        {esAdmin ? <Invitar destacar={verSeccion === 'invitar'} /> : null}

        <section>
          <SectionHeader title="Cuenta" />
          <Card className="p-0">
            <button
              type="button"
              onClick={() => {
                haptic('medium')
                void signOut()
              }}
              className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left active:scale-[0.99]"
            >
              <span className="flex size-9 items-center justify-center rounded-full bg-expense-soft text-expense-text">
                <SignOutIcon size={17} weight="fill" />
              </span>
              <span className="flex-1 text-[14px] font-medium text-ink">Cerrar sesión</span>
            </button>
          </Card>
          <p className="mt-2 px-1 text-[12px] text-ink-faint">{profile.email}</p>
        </section>
      </div>

      {hoja?.tipo === 'perfil' ? (
        <PerfilForm miembro={hoja.miembro} integrantes={data.members} onCerrar={cerrar} />
      ) : null}

      {hoja?.tipo === 'ingreso' ? (
        <IngresoForm memberId={membership.id} fuente={hoja.fuente} onCerrar={cerrar} />
      ) : null}

      {hoja?.tipo === 'aportacion' && yo ? (
        <AportacionForm memberId={yo.id} actual={yo.fixedContributionCents} onCerrar={cerrar} />
      ) : null}

      {hoja?.tipo === 'hogar' ? (
        <HogarForm
          nombreActual={data.name}
          contarActual={data.countExpectedIncome}
          anclaActual={data.periodAnchorDay}
          onCerrar={cerrar}
        />
      ) : null}

      {hoja?.tipo === 'cuenta-nueva' ? (
        <CuentaNuevaForm
          integrantes={data.members}
          miMemberId={membership.id}
          onCerrar={cerrar}
        />
      ) : null}

      {hoja?.tipo === 'cuenta' ? (
        <CuentaForm cuenta={hoja.cuenta} onCerrar={cerrar} />
      ) : null}

      {hoja?.tipo === 'integrante' ? (
        <IntegranteForm
          miembro={hoja.miembro}
          integrantes={data.members}
          esPersonal={esPersonal}
          onCerrar={cerrar}
        />
      ) : null}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Gastos fijos
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Puerta de entrada a los gastos fijos.
 *
 * Viven en su propia pantalla y no aquí dentro: son una lista que se consulta a
 * mitad de quincena —«¿ya pagué la luz?»—, no un ajuste que se toca una vez. El
 * enlace anuncia cuántos quedan por pagar para que no haya que entrar a saberlo.
 */
function EnlaceGastosFijos() {
  const navigate = useNavigate()
  const fijos = useRecurring()

  const activos = (fijos.data ?? []).filter((f) => f.active)
  const pendientes = activos.filter((f) => !f.postedThisPeriod)
  const porPagar = pendientes.reduce((total, f) => total + f.projectedAmountCents, 0)

  const detalle = fijos.isPending
    ? 'Cargando…'
    : fijos.isError
      ? 'La renta, la luz, las suscripciones.'
      : activos.length === 0
        ? 'Define la renta, la luz o el gimnasio y dejamos de contarlos como dinero libre.'
        : pendientes.length === 0
          ? `${activos.length} activos · todos pagados este periodo.`
          : `${activos.length} activos · ${formatMoney(porPagar, { hideZeroCents: true })} por pagar.`

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={() => {
          haptic('light')
          navigate('/fijos')
        }}
        className="flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left active:scale-[0.99]"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
          <RepeatIcon size={20} weight="fill" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold text-ink">Gastos fijos</span>
          <span className="block text-[12px] leading-snug text-ink-soft">{detalle}</span>
        </span>
        <CaretRightIcon size={16} className="shrink-0 text-ink-faint" />
      </button>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Tu perfil
// ═══════════════════════════════════════════════════════════════════════════

const TRATO: Record<Treatment, { etiqueta: string; ejemplo: string }> = {
  FEMININE: { etiqueta: 'Femenino', ejemplo: '«Estás lista para ahorrar»' },
  MASCULINE: { etiqueta: 'Masculino', ejemplo: '«Estás listo para ahorrar»' },
  NEUTRAL: { etiqueta: 'Neutral', ejemplo: '«Todo listo para ahorrar»' },
}

function MiPerfil({ miembro, onEditar }: { miembro: Member; onEditar: () => void }) {
  const paleta = personPalette(miembro.personColorToken)
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <PersonAvatar name={nombreDe(miembro)} token={miembro.personColorToken} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-[17px] font-bold tracking-[-0.02em] text-ink">
            {nombreDe(miembro)}
          </p>
          <p className="truncate text-[12px] text-ink-soft">
            {paleta.label} · trato {TRATO[miembro.treatment].etiqueta.toLowerCase()}
          </p>
        </div>
      </div>
      <BotonSecundario onClick={onEditar}>Editar mi perfil</BotonSecundario>
    </Card>
  )
}

/**
 * Apodo, trato y color.
 *
 * Los tres son de quien los lleva, así que este formulario no pregunta permisos
 * ni pide ser administrador: escribe sobre `PATCH /household/me`.
 */
function PerfilForm({
  miembro,
  integrantes,
  onCerrar,
}: {
  miembro: Member
  integrantes: readonly Member[]
  onCerrar: () => void
}) {
  const guardar = useActualizarMiPerfil()
  const [apodo, setApodo] = useState(miembro.nickname ?? '')
  const [trato, setTrato] = useState<Treatment>(miembro.treatment)
  const [color, setColor] = useState(miembro.personColorToken)
  const [error, setError] = useState<string | null>(null)

  // Los colores que ya identifican a otra persona quedan fuera: dos integrantes
  // del mismo tono vuelven ilegible cualquier vista comparativa, y la base lo
  // rechaza de todos modos.
  const tomados = new Set(
    integrantes.filter((m) => m.id !== miembro.id).map((m) => m.personColorToken),
  )

  const enviar = (): void => {
    setError(null)
    guardar.mutate(
      { nickname: apodo.trim() === '' ? null : apodo.trim(), treatment: trato, personColorToken: color },
      {
        onSuccess: onCerrar,
        onError: (e) => setError(mensajeDeError(e, 'No pudimos guardar tu perfil.')),
      },
    )
  }

  return (
    <FormSheet
      titulo="Tu perfil"
      onCerrar={onCerrar}
      accion={
        <Guardar onClick={enviar} guardando={guardar.isPending}>
          Guardar perfil
        </Guardar>
      }
    >
      <AvisoError mensaje={error} />

      <Campo
        label="Cómo quieres que te llamemos"
        ayuda={`Si lo dejas vacío usamos ${miembro.user.displayName}.`}
      >
        <Texto
          value={apodo}
          onChange={setApodo}
          placeholder={miembro.user.displayName}
          maxLength={40}
        />
      </Campo>

      <Campo
        label="Cómo te hablamos"
        ayuda="Decide el género de los avisos que te manda la app."
      >
        <Segmentos
          columnas={3}
          value={trato}
          onChange={setTrato}
          opciones={
            (['FEMININE', 'MASCULINE', 'NEUTRAL'] as const).map((t) => ({
              value: t,
              label: TRATO[t].etiqueta,
            })) satisfies OpcionSegmento<Treatment>[]
          }
        />
        <p className="mt-1 text-[12px] italic text-ink-faint">{TRATO[trato].ejemplo}</p>
      </Campo>

      <Campo
        label="Tu color"
        ayuda="Es cómo te reconoces en las gráficas y en cada movimiento, sin leer nombres."
      >
        <div className="grid grid-cols-4 gap-2">
          {PERSON_TOKENS.map((token) => {
            const paleta = personPalette(token)
            const ocupado = tomados.has(token)
            const activo = token === color
            return (
              <button
                key={token}
                type="button"
                disabled={ocupado}
                onClick={() => {
                  haptic('selection')
                  setColor(token)
                }}
                aria-pressed={activo}
                aria-label={ocupado ? `${paleta.label}, ya lo usa alguien` : paleta.label}
                className={cn(
                  'flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-card border',
                  'transition-transform duration-150',
                  ocupado
                    ? 'cursor-not-allowed border-line bg-surface-sunken opacity-40'
                    : activo
                      ? 'border-primary bg-primary-soft active:scale-95'
                      : 'border-line bg-surface active:scale-95',
                )}
              >
                <span
                  className="size-6 rounded-full"
                  style={{ background: paleta.cssVar }}
                  aria-hidden
                />
                <span className="text-[10px] leading-none text-ink-soft">{paleta.label}</span>
              </button>
            )
          })}
        </div>
      </Campo>
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Apariencia
// ═══════════════════════════════════════════════════════════════════════════

const TEMAS: ReadonlyArray<OpcionSegmento<ThemePreference>> = [
  { value: 'light', label: 'Claro', detalle: 'Siempre' },
  { value: 'dark', label: 'Oscuro', detalle: 'Siempre' },
  { value: 'system', label: 'Automático', detalle: 'Del sistema' },
]

/**
 * Selector de tema y de color.
 *
 * Se aplica al tocar, sin botón de guardar: es lo único de esta pantalla que se
 * ve al instante y no toca dinero, así que confirmarlo sería un paso de más. Y
 * el cambio se ve en el acto: la barra de abajo cambia de color bajo el dedo,
 * que es la vista previa más honesta que puede haber.
 */
function Apariencia() {
  const [preferencia, setPreferencia] = useState<ThemePreference>(() => getStoredPreference())
  const [paleta, setPaleta] = useState<PaletaId>(() => getPaleta())

  return (
    <section>
      <SectionHeader title="Apariencia" />
      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
            <PaletteIcon size={20} weight="fill" />
          </span>
          <p className="flex-1 text-[13px] leading-snug text-ink-soft">
            En automático seguimos el modo de tu teléfono, incluido el cambio de noche.
          </p>
        </div>
        <Segmentos
          columnas={3}
          opciones={TEMAS}
          value={preferencia}
          onChange={(v) => {
            setPreferencia(v)
            applyTheme(v)
          }}
        />

        <div className="mt-2 border-t border-line pt-4">
          <p className="text-[14px] font-semibold text-ink">Color de la app</p>
          <p className="mt-0.5 text-[12px] leading-snug text-ink-soft">
            Cambia el inicio, la barra de abajo y los botones. Los colores que dicen algo —lo
            que entra, lo que sale, el ahorro— no cambian.
          </p>
          <div
            role="group"
            aria-label="Color de la app"
            className="mt-3.5 grid grid-cols-5 gap-x-1.5 gap-y-3.5"
          >
            {PALETAS.map((p) => (
              <MuestraPaleta
                key={p.id}
                id={p.id}
                nombre={p.nombre}
                elegida={paleta === p.id}
                onElegir={() => {
                  if (paleta === p.id) return
                  haptic('selection')
                  setPaleta(p.id)
                  applyPaleta(p.id)
                }}
              />
            ))}
          </div>
        </div>
      </Card>
    </section>
  )
}

/**
 * Una paleta, en miniatura.
 *
 * El círculo no lleva colores escritos a mano: lleva su propio `data-paleta`,
 * así que `--navy` y `--brand` se resuelven a los de ESA paleta aunque la app
 * tenga otra puesta (ver la nota de las paletas en `tokens.css`). La muestra
 * es por construcción lo que se va a ver, y no puede desfasarse de los tokens.
 *
 * Bloque arriba y acento abajo, en diagonal, porque son las dos piezas que más
 * superficie ocupan del inicio; el punto es el color del dato y los enlaces.
 */
function MuestraPaleta({
  id,
  nombre,
  elegida,
  onElegir,
}: {
  id: PaletaId
  nombre: string
  elegida: boolean
  onElegir: () => void
}) {
  return (
    <button
      type="button"
      onClick={onElegir}
      aria-pressed={elegida}
      aria-label={`Color ${nombre}`}
      className="flex min-h-[44px] flex-col items-center gap-1.5 active:scale-95"
    >
      <span
        data-paleta={id}
        className={cn(
          'relative flex size-12 items-center justify-center rounded-full transition-shadow duration-150',
          elegida ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface' : 'ring-1 ring-line',
        )}
        style={{
          background: 'linear-gradient(135deg, var(--navy) 0 52%, var(--brand) 52% 100%)',
        }}
      >
        <span
          aria-hidden="true"
          className="absolute bottom-1.5 right-1.5 size-2.5 rounded-full bg-primary ring-2 ring-brand"
        />
        {/* La palomita va en su propia pastilla de tinta: el centro del
            círculo cae justo en la diagonal, y una palomita blanca suelta se
            perdía sobre la mitad clara. */}
        {elegida ? (
          <span className="relative flex size-6 items-center justify-center rounded-full bg-navy text-on-navy ring-2 ring-brand">
            <CheckIcon size={13} weight="bold" />
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          'max-w-full truncate text-[10.5px] leading-tight',
          elegida ? 'font-semibold text-ink' : 'text-ink-soft',
        )}
      >
        {nombre}
      </span>
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Nómina
// ═══════════════════════════════════════════════════════════════════════════

const FRECUENCIAS: ReadonlyArray<OpcionSegmento<PayFrequency>> = [
  { value: 'SEMI_MONTHLY', label: 'Quincenal', detalle: 'Dos veces al mes' },
  { value: 'MONTHLY', label: 'Mensual', detalle: 'Una vez al mes' },
  { value: 'BIWEEKLY', label: 'Catorcenal', detalle: 'Cada 14 días' },
  { value: 'WEEKLY', label: 'Semanal', detalle: 'Cada 7 días' },
  {
    value: 'IRREGULAR',
    label: 'Sin calendario',
    detalle: 'Llega cuando llega',
  },
]

const REGLAS_INHABIL: ReadonlyArray<OpcionSegmento<NonBusinessDayRule>> = [
  { value: 'PREVIOUS_BUSINESS_DAY', label: 'Antes', detalle: 'Día hábil previo' },
  { value: 'NEXT_BUSINESS_DAY', label: 'Después', detalle: 'Día hábil siguiente' },
  { value: 'EXACT', label: 'Exacto', detalle: 'Aunque sea inhábil' },
]

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const

/** `YYYY-MM-DD` a texto legible sin pasar por `Date`, que cambiaría el día por huso. */
function fechaLegible(fecha: PlainDate): string {
  const { month, day } = toParts(fecha)
  return `${day} de ${MESES[month - 1]}`
}

function ListaIngresos({
  fuentes,
  onEditar,
  onCrear,
}: {
  fuentes: readonly IncomeSource[]
  onEditar: (fuente: IncomeSource) => void
  onCrear: () => void
}) {
  if (fuentes.length === 0) {
    return (
      <Card className="flex flex-col gap-3">
        <p className="text-[13px] leading-snug text-ink-soft">
          Sin ingresos configurados. Sin al menos uno no hay periodo de gasto ni disponible.
        </p>
        <BotonSecundario onClick={onCrear}>Agregar un ingreso</BotonSecundario>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col gap-2 p-0">
      {fuentes.map((f, i) => (
        <button
          key={f.id}
          type="button"
          onClick={() => {
            haptic('light')
            onEditar(f)
          }}
          className={cn(
            'flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left active:scale-[0.99]',
            i > 0 && 'border-t border-line',
          )}
        >
          <span
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-full',
              f.kind === 'FIXED'
                ? 'bg-primary-soft text-primary-text'
                : 'bg-warning-soft text-warning',
            )}
          >
            {f.kind === 'FIXED' ? <WalletIcon size={20} weight="fill" /> : <ChartLineUpIcon size={20} weight="fill" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[15px] font-semibold text-ink">{f.name}</span>
              {!f.active ? (
                <span className="shrink-0 rounded-pill bg-surface-sunken px-1.5 py-0.5 text-[10px] text-ink-faint">
                  pausado
                </span>
              ) : null}
            </span>
            <span className="block truncate text-[12px] text-ink-soft">
              {FRECUENCIA[f.frequency]} ·{' '}
              {f.kind === 'VARIABLE' ? 'variable' : 'fijo'}
              {f.nextPayDates[0] ? ` · el próximo el ${fechaLegible(f.nextPayDates[0])}` : ''}
            </span>
          </span>
          <span data-money className="shrink-0 text-[15px] font-semibold text-ink">
            {formatMoney(f.netAmountCents, { hideZeroCents: true })}
          </span>
        </button>
      ))}
      <div className="border-t border-line p-3">
        <BotonSecundario onClick={onCrear}>Agregar otro ingreso</BotonSecundario>
      </div>
    </Card>
  )
}

const TIPOS_INGRESO: OpcionSegmento<IncomeKind>[] = [
  { value: 'FIXED', label: 'Fijo', detalle: 'Llega siempre' },
  { value: 'VARIABLE', label: 'Variable', detalle: 'Llega a veces' },
]

/**
 * Alta y edición de una fuente de ingreso.
 *
 * `kind` no es un detalle administrativo: un ingreso variable **no cuenta**
 * para el disponible hasta que se confirma. Contar con una comisión antes de
 * recibirla es la forma más común de sobregirarse, así que la app prefiere
 * quedarse corta y avisar cuando llegue.
 */
function IngresoForm({
  memberId,
  fuente,
  onCerrar,
}: {
  memberId: string
  fuente: IncomeSource | null
  onCerrar: () => void
}) {
  const [nombre, setNombre] = useState(fuente?.name ?? '')
  const [tipo, setTipo] = useState<IncomeKind>(fuente?.kind ?? 'FIXED')
  const [frecuencia, setFrecuencia] = useState<PayFrequency>(fuente?.frequency ?? 'SEMI_MONTHLY')
  const [neto, setNeto] = useState(fuente?.netAmountCents ?? 0)
  const [fecha, setFecha] = useState<string>(fuente?.anchorDate ?? '')
  // Los dos días de pago. `0` significa «último día del mes», que es como se
  // captura la segunda quincena en México y lo que espera el servidor.
  const [diaA, setDiaA] = useState<number>(fuente?.anchorDayA ?? 15)
  const [diaB, setDiaB] = useState<number>(fuente?.anchorDayB ?? 0)
  const [regla, setRegla] = useState<NonBusinessDayRule>(
    fuente?.nonBusinessDayRule ?? 'PREVIOUS_BUSINESS_DAY',
  )
  const [ahorro, setAhorro] = useState(String(fuente?.defaultSavingsRatePct ?? 20))
  const [activo, setActivo] = useState(fuente?.active ?? true)
  const [error, setError] = useState<string | null>(null)

  const guardar = useGuardarIngreso(memberId)
  const borrar = useBorrarIngreso(memberId)

  const requiereFecha = frecuencia === 'BIWEEKLY' || frecuencia === 'WEEKLY'
  const requiereDias = frecuencia === 'SEMI_MONTHLY' || frecuencia === 'MONTHLY'
  // Dos quincenas el mismo día son una sola nómina al mes: el calendario
  // deduplica las fechas y quien lo capturó vería la mitad de su sueldo.
  const diasRepetidos = frecuencia === 'SEMI_MONTHLY' && diaA === diaB
  const pct = Number(ahorro)
  const pctValido = ahorro !== '' && Number.isInteger(pct) && pct >= 0 && pct <= 100
  const listo =
    nombre.trim() !== '' &&
    neto > 0 &&
    pctValido &&
    !diasRepetidos &&
    (!requiereFecha || /^\d{4}-\d{2}-\d{2}$/.test(fecha))

  /**
   * Al cambiar de frecuencia se deshace el único choque posible.
   *
   * Pasar de mensual a fin de mes a quincenal deja las dos anclas en «último
   * día», que no es un calendario quincenal. En vez de abrir el formulario ya
   * en error, la primera quincena vuelve al 15 —el reparto habitual en
   * México— y quien cobre otro día lo cambia con un toque.
   */
  const cambiarFrecuencia = (siguiente: PayFrequency): void => {
    setFrecuencia(siguiente)
    if (siguiente === 'SEMI_MONTHLY' && diaA === diaB) {
      setDiaA(diaA === 15 ? 30 : 15)
    }
  }

  const enviar = async (): Promise<void> => {
    setError(null)
    // En catorcenal y semanal las anclas de mes no describen nada —la fase la
    // fija `anchorDate`—, pero el servidor exige `anchorDayA` igual, así que se
    // manda un valor inocuo en vez de dejar que el esquema rechace el alta.
    const anchorDayA = requiereDias ? diaA : 1
    const anchorDayB = frecuencia === 'SEMI_MONTHLY' ? diaB : null

    try {
      await guardar.mutateAsync({
        id: fuente?.id,
        name: nombre.trim(),
        kind: tipo,
        frequency: frecuencia,
        netAmountCents: neto,
        anchorDayA,
        anchorDayB,
        anchorDate: requiereFecha ? (fecha as PlainDate) : null,
        nonBusinessDayRule: regla,
        defaultSavingsRatePct: pct,
        active: activo,
      })
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos guardar este ingreso.'))
    }
  }

  const eliminar = async (): Promise<void> => {
    if (!fuente) return
    setError(null)
    try {
      await borrar.mutateAsync(fuente.id)
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos eliminar este ingreso.'))
    }
  }

  return (
    <FormSheet
      titulo={fuente ? fuente.name : 'Nuevo ingreso'}
      onCerrar={onCerrar}
      accion={
        <Guardar onClick={() => void enviar()} disabled={!listo} guardando={guardar.isPending} />
      }
    >
      <AvisoError mensaje={error} />

      <Campo label="Cómo se llama" ayuda="Salario, Comisiones, Renta del departamento…">
        <Texto value={nombre} onChange={setNombre} placeholder="Salario" maxLength={60} />
      </Campo>

      <Campo
        label="Qué tan seguro es"
        ayuda={
          tipo === 'FIXED'
            ? 'Lo contamos en tu disponible desde antes de que llegue.'
            : 'No lo contamos hasta que confirmes que llegó. Así el disponible nunca promete de más.'
        }
      >
        <Segmentos opciones={TIPOS_INGRESO} value={tipo} onChange={setTipo} />
      </Campo>

      <Campo label="Cada cuándo llega">
        <Segmentos opciones={FRECUENCIAS} value={frecuencia} onChange={cambiarFrecuencia} />
      </Campo>

      {requiereFecha ? (
        <Campo
          label="Fecha del último pago"
          ayuda="La necesitamos para calcular los siguientes: catorcenal y semanal no caen en un día fijo del mes."
        >
          <Texto type="date" value={fecha} onChange={setFecha} />
        </Campo>
      ) : null}

      {/* El día de pago es editable, y no un dato que la app deduzca.
          Antes se derivaba de la frecuencia —15 y último para quincenal, último
          para mensual— y no había forma de corregirlo: quien cobra el 5 y el 20
          veía su nómina anunciada dos semanas fuera de sitio, y con ella el
          periodo, el disponible y los avisos del día de pago. */}
      {requiereDias ? (
        frecuencia === 'SEMI_MONTHLY' ? (
          <>
            <Campo label="Primer día de pago">
              <DiaDelMes value={diaA} onChange={setDiaA} />
            </Campo>
            <Campo
              label="Segundo día de pago"
              ayuda="Si tu segunda quincena cae siempre a fin de mes, elige «Último día»: así también acierta en febrero."
              error={diasRepetidos ? 'Las dos quincenas no pueden caer el mismo día.' : undefined}
            >
              <DiaDelMes value={diaB} onChange={setDiaB} />
            </Campo>
          </>
        ) : (
          <Campo
            label="Qué día te pagan"
            ayuda="Si te pagan a fin de mes, elige «Último día»: los meses de 30 y de 28 se ajustan solos."
          >
            <DiaDelMes value={diaA} onChange={setDiaA} />
          </Campo>
        )
      ) : null}

      <Campo label={tipo === 'FIXED' ? 'Cuánto recibes cada pago (neto)' : 'Cuánto sueles recibir'}>
        <Monto cents={neto} onChange={setNeto} />
      </Campo>

      <Campo
        label="Si el día de pago cae inhábil"
        ayuda="Fin de semana o festivo bancario. Casi todas las empresas en México adelantan."
      >
        <Segmentos columnas={3} opciones={REGLAS_INHABIL} value={regla} onChange={setRegla} />
      </Campo>

      <Campo
        label="Qué parte quieres ahorrar"
        ayuda="En porcentaje, de 0 a 100. Lo usamos para proponerte cuánto apartar cuando llegue."
        error={pctValido ? undefined : 'Escribe un número entero entre 0 y 100.'}
      >
        <Texto
          value={ahorro}
          onChange={setAhorro}
          inputMode="numeric"
          maxLength={3}
          placeholder="20"
          invalido={!pctValido}
        />
      </Campo>

      {fuente ? (
        <>
          <Interruptor
            label="Sigo recibiendo este ingreso"
            ayuda="Al pausarlo deja de contar para el disponible, pero su historial se conserva."
            value={activo}
            onChange={setActivo}
          />
          <button
            type="button"
            onClick={() => void eliminar()}
            disabled={borrar.isPending}
            className="min-h-[48px] rounded-card text-[14px] font-semibold text-expense-text active:scale-[0.99]"
          >
            Eliminar este ingreso
          </button>
        </>
      ) : null}
    </FormSheet>
  )
}

/** `0` es «último día del mes», la convención que usan el servidor y el motor. */
const ULTIMO_DIA_DEL_MES = 0

/**
 * Selector de día del mes.
 *
 * Rejilla de números y no un campo de texto: el día de pago se sabe de memoria
 * y escribirlo en un teclado numérico invita a los errores que este selector no
 * permite —un 0, un 45, un campo vacío—. Y no un `<input type="date">`, porque
 * esto no es una fecha sino una regla que se repite cada mes.
 *
 * `conUltimo` añade «Último día» como opción de primera clase, con su propio
 * botón ancho, en vez de esconderla tras el 31: la segunda quincena a fin de mes
 * es el caso más común de México, y elegir 31 daría el 31 de marzo pero un 28 en
 * febrero. Con esta opción no hay nada que recortar. Se apaga donde el valor no
 * admite esa idea, como el arranque del periodo del hogar, que va de 1 a 28.
 */
function DiaDelMes({
  value,
  onChange,
  max = 31,
  conUltimo = true,
}: {
  value: number
  onChange: (v: number) => void
  max?: number
  conUltimo?: boolean
}) {
  const elegir = (dia: number): void => {
    haptic('selection')
    onChange(dia)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: max }, (_, i) => i + 1).map((dia) => {
          const activo = dia === value
          return (
            <button
              key={dia}
              type="button"
              onClick={() => elegir(dia)}
              aria-pressed={activo}
              aria-label={`Día ${dia}`}
              className={cn(
                'flex min-h-[40px] items-center justify-center rounded-chip border text-[14px] tabular-nums',
                'transition-colors duration-150 active:scale-[0.95]',
                activo
                  ? 'border-primary bg-primary-soft font-semibold text-ink'
                  : 'border-line bg-surface text-ink-soft',
              )}
            >
              {dia}
            </button>
          )
        })}
      </div>
      {conUltimo ? (
        <button
          type="button"
          onClick={() => elegir(ULTIMO_DIA_DEL_MES)}
          aria-pressed={value === ULTIMO_DIA_DEL_MES}
          className={cn(
            'flex min-h-[44px] items-center justify-center rounded-card border text-[14px]',
            'transition-colors duration-150 active:scale-[0.98]',
            value === ULTIMO_DIA_DEL_MES
              ? 'border-primary bg-primary-soft font-semibold text-ink'
              : 'border-line bg-surface text-ink-soft',
          )}
        >
          Último día del mes
        </button>
      ) : null}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Aportación al fondo común
// ═══════════════════════════════════════════════════════════════════════════

function AportacionForm({
  memberId,
  actual,
  onCerrar,
}: {
  memberId: string
  actual: number
  onCerrar: () => void
}) {
  const [cents, setCents] = useState(actual)
  const [error, setError] = useState<string | null>(null)
  const actualizar = useActualizarIntegrante()

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      await actualizar.mutateAsync({ memberId, fixedContributionCents: cents })
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos guardar tu aportación.'))
    }
  }

  return (
    <FormSheet
      titulo="Tu aportación"
      onCerrar={onCerrar}
      accion={<Guardar onClick={() => void enviar()} guardando={actualizar.isPending} />}
    >
      <AvisoError mensaje={error} />
      <Campo
        label="Monto fijo mensual"
        ayuda="Cuánto pones cada mes para los gastos compartidos. Se resta de tu dinero libre y se suma al del hogar."
      >
        <Monto cents={cents} onChange={setCents} />
      </Campo>
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Cuentas
// ═══════════════════════════════════════════════════════════════════════════

function ListaCuentas({
  cuentas,
  onAbrir,
  onCrear,
}: {
  cuentas: readonly Account[]
  onAbrir: (cuenta: Account) => void
  onCrear: () => void
}) {
  if (cuentas.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<WalletIcon size={32} weight="fill" />}
          title="Todavía no hay cuentas"
          description="Registra dónde tienes el dinero —tu cuenta del banco, el efectivo, los vales— y podremos decirte cuánto te queda disponible."
          action={<BotonSecundario onClick={onCrear}>Agregar una cuenta</BotonSecundario>}
        />
      </Card>
    )
  }

  // La dona deja fuera los vales a propósito: son dinero real, pero solo
  // compran despensa. Mezclarlos con el efectivo pintaría una capacidad de pago
  // que no existe.
  const gastables = cuentas.filter((c) => c.type !== 'MEAL_VOUCHER' && c.balanceCents > 0)
  const ordenadas = [...gastables].sort((a, b) => b.balanceCents - a.balanceCents)
  const segments: DonutSegment[] = ordenadas.map((c, i) => ({
    key: c.id,
    label: c.name,
    value: c.balanceCents,
    color: chartColor(i),
  }))
  const total = segments.reduce((s, seg) => s + seg.value, 0)
  const vales = cuentas.filter((c) => c.type === 'MEAL_VOUCHER')
  const totalVales = vales.reduce((s, c) => s + c.balanceCents, 0)

  return (
    <div className="flex flex-col gap-3">
      {segments.length > 0 ? (
        <Card className="flex items-center gap-4">
          <Donut
            segments={segments}
            centerValue={formatMoney(total, { hideZeroCents: true })}
            centerLabel="En tus cuentas"
          />
          <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
            {segments.slice(0, 4).map((segment, i) => (
              <li key={segment.key} className="flex items-baseline gap-2">
                <span
                  aria-hidden="true"
                  className="size-2 shrink-0 translate-y-[-1px] rounded-full"
                  style={{ background: chartColor(i) }}
                />
                <span className="truncate text-[13px] leading-tight text-ink-soft">
                  {segment.label}
                </span>
                <span data-money className="ml-auto text-[13px] font-semibold text-ink">
                  {formatMoney(segment.value, { hideZeroCents: true })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card className="divide-y divide-line p-0">
        {cuentas.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              haptic('light')
              onAbrir(c)
            }}
            className="flex w-full items-center gap-3 px-4 py-3 text-left active:scale-[0.99]"
          >
            <span
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full',
                c.type === 'MEAL_VOUCHER'
                  ? 'bg-warning-soft text-warning'
                  : 'bg-surface-sunken text-ink-soft',
              )}
            >
              {c.type === 'MEAL_VOUCHER' ? (
                <BasketIcon size={17} weight="fill" />
              ) : (
                <WalletIcon size={17} weight="fill" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-medium text-ink">{c.name}</p>
              <p className="truncate text-[12px] text-ink-soft">
                {TIPO_CUENTA[c.type]}
                {' · '}
                {c.owner ? c.owner.user.displayName : 'Del hogar'}
                {c.institution ? ` · ${c.institution}` : ''}
              </p>
            </div>
            <span data-money className="shrink-0 text-[14px] font-semibold text-ink">
              {formatMoney(c.balanceCents, { hideZeroCents: true })}
            </span>
            <CaretRightIcon size={16} className="shrink-0 text-ink-faint" />
          </button>
        ))}
      </Card>

      {vales.length > 0 ? (
        <p className="flex items-start gap-1.5 px-1 text-[12px] leading-snug text-ink-faint">
          <BasketIcon size={14} className="mt-0.5 shrink-0" />
          Tus {formatMoney(totalVales, { hideZeroCents: true })} en vales van en bolsa aparte:
          solo pagan despensa, así que no se suman al dinero con el que puedes cubrir el resto.
        </p>
      ) : null}
    </div>
  )
}

const TIPOS_CUENTA: ReadonlyArray<OpcionSegmento<AccountType>> = [
  { value: 'CHECKING', label: 'Débito', detalle: 'Cuenta de banco' },
  { value: 'CASH', label: 'Efectivo', detalle: 'Lo que traes encima' },
  { value: 'SAVINGS', label: 'Ahorro', detalle: 'Apartado, no gasto' },
  { value: 'INVESTMENT', label: 'Inversión', detalle: 'Cetes, fondos' },
  { value: 'MEAL_VOUCHER', label: 'Vales', detalle: 'Solo despensa' },
]

function CuentaNuevaForm({
  integrantes,
  miMemberId,
  onCerrar,
}: {
  integrantes: readonly Member[]
  miMemberId: string
  onCerrar: () => void
}) {
  const [nombre, setNombre] = useState('')
  const [tipo, setTipo] = useState<AccountType>('CHECKING')
  const [dueno, setDueno] = useState<string>(miMemberId)
  const [banco, setBanco] = useState('')
  const [saldo, setSaldo] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const crear = useCrearCuenta()

  // Con una sola persona en el hogar, «mía» y «del hogar» describen lo mismo:
  // preguntarlo solo añadiría una decisión sin consecuencia.
  const eligeDueno = integrantes.length > 1
  const opcionesDueno: ReadonlyArray<OpcionSegmento<string>> = [
    ...integrantes.map((m) => ({
      value: m.id,
      label: m.id === miMemberId ? 'Mía' : m.user.displayName,
      detalle: m.id === miMemberId ? m.user.displayName : 'Personal',
    })),
    { value: DUENO_COMUN, label: 'Del hogar', detalle: 'Fondo común' },
  ]

  const nombreValido = nombre.trim().length >= 2
  const saldoValido = tipo !== 'MEAL_VOUCHER' || saldo >= 0

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      await crear.mutateAsync({
        name: nombre.trim(),
        type: tipo,
        // `null` no es un descuido: significa cuenta del fondo común, y por eso
        // el servidor lo exige explícito.
        ownerMemberId: dueno === DUENO_COMUN ? null : dueno,
        institution: banco.trim() || undefined,
        initialBalanceCents: saldo,
      })
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos crear la cuenta.'))
    }
  }

  return (
    <FormSheet
      titulo="Nueva cuenta"
      onCerrar={onCerrar}
      accion={
        <Guardar
          onClick={() => void enviar()}
          disabled={!nombreValido || !saldoValido}
          guardando={crear.isPending}
        >
          Crear cuenta
        </Guardar>
      }
    >
      <AvisoError mensaje={error} />

      <Campo label="Nombre">
        <Texto
          value={nombre}
          onChange={setNombre}
          placeholder="Cuenta BBVA"
          maxLength={60}
          invalido={nombre.length > 0 && !nombreValido}
        />
      </Campo>

      <Campo
        label="Tipo"
        ayuda={
          tipo === 'MEAL_VOUCHER'
            ? 'Los vales van en bolsa aparte: son dinero real, pero solo pagan despensa, así que nunca se suman al efectivo con el que cubres lo demás.'
            : 'Débito y efectivo cuentan como dinero disponible; ahorro e inversión se muestran aparte.'
        }
      >
        <Segmentos opciones={TIPOS_CUENTA} value={tipo} onChange={setTipo} />
      </Campo>

      {eligeDueno ? (
        <Campo
          label="De quién es"
          ayuda="Las cuentas del hogar alimentan el fondo común; las personales, solo tu dinero libre."
        >
          <Segmentos opciones={opcionesDueno} value={dueno} onChange={setDueno} />
        </Campo>
      ) : null}

      <Campo label="Banco o emisor (opcional)">
        <Texto value={banco} onChange={setBanco} placeholder="BBVA" maxLength={60} />
      </Campo>

      <Campo
        label="Saldo actual"
        ayuda="El que ves hoy en tu app del banco. A partir de ahí lo movemos nosotros."
      >
        <Monto cents={saldo} onChange={setSaldo} />
      </Campo>
    </FormSheet>
  )
}

/**
 * Edición de una cuenta.
 *
 * El saldo se captura como el valor **real** —lo que dice el banco—, no como la
 * diferencia: nadie sabe de memoria cuánto le falta a la app, pero todo el
 * mundo tiene el saldo bueno a la vista. La diferencia la calculamos aquí y se
 * muestra para que el ajuste no sea a ciegas.
 */
function CuentaForm({ cuenta, onCerrar }: { cuenta: Account; onCerrar: () => void }) {
  const [nombre, setNombre] = useState(cuenta.name)
  const [banco, setBanco] = useState(cuenta.institution ?? '')
  const [enDisponible, setEnDisponible] = useState(cuenta.includeInAvailable)
  const [saldo, setSaldo] = useState(cuenta.balanceCents)
  const [confirmaRetiro, setConfirmaRetiro] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const actualizar = useActualizarCuenta()
  const conciliar = useConciliarCuenta()
  const archivar = useArchivarCuenta()
  const borrar = useBorrarCuenta()

  /**
   * Archivar o borrar no es una preferencia: lo decide la cuenta.
   *
   * Con un solo movimiento detrás, borrarla dejaría ese movimiento sin
   * responder «¿con qué se pagó?», y los reportes de meses cerrados dejarían de
   * cuadrar. Sin nada detrás no hay historia que proteger, y obligar a archivar
   * un alta equivocada de hace dos minutos la deja para siempre en la lista de
   * archivadas. Así que se ofrece una acción, la que corresponde, en vez de dos
   * y una explicación de cuál elegir.
   */
  const soloArchivable = cuenta.hasHistory
  const retirando = soloArchivable ? archivar.isPending : borrar.isPending

  const esVales = cuenta.type === 'MEAL_VOUCHER'
  const nombreValido = nombre.trim().length >= 2
  const saldoValido = !esVales || saldo >= 0
  const delta = saldo - cuenta.balanceCents

  const guardando = actualizar.isPending || conciliar.isPending

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      const cambioDatos =
        nombre.trim() !== cuenta.name ||
        (banco.trim() || null) !== cuenta.institution ||
        enDisponible !== cuenta.includeInAvailable

      if (cambioDatos) {
        await actualizar.mutateAsync({
          id: cuenta.id,
          name: nombre.trim(),
          institution: banco.trim() || null,
          includeInAvailable: enDisponible,
        })
      }
      // La conciliación va aparte porque es otra operación del servidor: deja
      // rastro en la bitácora de quién ajustó el saldo y de cuánto fue.
      if (delta !== 0) {
        await conciliar.mutateAsync({ id: cuenta.id, balanceCents: saldo })
      }
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos guardar los cambios.'))
    }
  }

  const retirarCuenta = async (): Promise<void> => {
    setError(null)
    try {
      if (soloArchivable) {
        await archivar.mutateAsync(cuenta.id)
      } else {
        await borrar.mutateAsync(cuenta.id)
      }
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(
        mensajeDeError(
          cause,
          soloArchivable ? 'No pudimos archivar la cuenta.' : 'No pudimos eliminar la cuenta.',
        ),
      )
      // Se vuelve al estado sin confirmar: dejar el botón rojo puesto después
      // de un fallo invita a picarlo otra vez esperando otro resultado.
      setConfirmaRetiro(false)
    }
  }

  return (
    <FormSheet
      titulo={cuenta.name}
      onCerrar={onCerrar}
      accion={
        <Guardar
          onClick={() => void enviar()}
          disabled={!nombreValido || !saldoValido}
          guardando={guardando}
        />
      }
    >
      <AvisoError mensaje={error} />

      <Campo label="Nombre">
        <Texto
          value={nombre}
          onChange={setNombre}
          maxLength={60}
          invalido={!nombreValido}
        />
      </Campo>

      <Campo label="Banco o emisor (opcional)">
        <Texto value={banco} onChange={setBanco} placeholder="BBVA" maxLength={60} />
      </Campo>

      <Campo
        label="Saldo real"
        ayuda="Escribe lo que dice tu banco ahora mismo, no la diferencia. Nosotros calculamos el ajuste."
        error={saldoValido ? undefined : 'El saldo de los vales no puede ser negativo.'}
      >
        <Monto cents={saldo} onChange={setSaldo} />
      </Campo>

      {delta !== 0 ? (
        <p className="flex items-center gap-2 rounded-card bg-surface-sunken px-3.5 py-2.5 text-[12px] leading-snug text-ink-soft">
          <ArrowsClockwiseIcon size={14} className="shrink-0" />
          Ajuste de {formatMoney(cuenta.balanceCents, { hideZeroCents: true })} a{' '}
          {formatMoney(saldo, { hideZeroCents: true })} ·{' '}
          <span data-money className="font-semibold text-ink">
            {formatMoney(delta, { showSign: true, hideZeroCents: true })}
          </span>
        </p>
      ) : null}

      {esVales ? (
        <p className="rounded-card bg-warning-soft px-3.5 py-2.5 text-[12px] leading-snug text-ink-soft">
          Los vales siempre cuentan, pero en su propia bolsa: pagan despensa y nada más.
        </p>
      ) : (
        <Interruptor
          label="Contar en el disponible"
          ayuda="Apágalo si es una cuenta que no tocas para el día a día. Seguirás viendo su saldo aquí."
          value={enDisponible}
          onChange={setEnDisponible}
        />
      )}

      <div className="flex flex-col gap-2 border-t border-line pt-4">
        {confirmaRetiro ? (
          <>
            <p className="text-[12px] leading-snug text-ink-soft">
              {soloArchivable
                ? 'La cuenta desaparece de la app pero sus movimientos se conservan: sin ellos, los reportes de meses anteriores dejarían de cuadrar.'
                : 'Esta cuenta no tiene ningún movimiento, así que se borra completa y no hay nada que recuperar después.'}
            </p>
            <button
              type="button"
              onClick={() => void retirarCuenta()}
              disabled={retirando}
              className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-card bg-expense-soft text-[14px] font-semibold text-expense-text active:scale-[0.98] disabled:opacity-70"
            >
              {retirando ? (
                <SpinnerGapIcon size={18} className="animate-spin" />
              ) : soloArchivable ? (
                <>
                  <ArchiveIcon size={16} weight="bold" />
                  Sí, archivar «{cuenta.name}»
                </>
              ) : (
                <>
                  <TrashIcon size={16} weight="bold" />
                  Sí, eliminar «{cuenta.name}»
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setConfirmaRetiro(false)}
              className="min-h-[44px] text-[13px] font-medium text-ink-soft underline underline-offset-4"
            >
              Mejor no
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              haptic('warning')
              setConfirmaRetiro(true)
            }}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-card border border-line bg-surface text-[14px] font-medium text-ink-soft active:scale-[0.98]"
          >
            {soloArchivable ? (
              <>
                <ArchiveIcon size={16} />
                Archivar cuenta
              </>
            ) : (
              <>
                <TrashIcon size={16} />
                Eliminar cuenta
              </>
            )}
          </button>
        )}
      </div>
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Preferencias del hogar
// ═══════════════════════════════════════════════════════════════════════════

function HogarForm({
  nombreActual,
  contarActual,
  anclaActual,
  onCerrar,
}: {
  nombreActual: string
  contarActual: boolean
  anclaActual: number
  onCerrar: () => void
}) {
  const [nombre, setNombre] = useState(nombreActual)
  const [contar, setContar] = useState(contarActual)
  const [ancla, setAncla] = useState(anclaActual)
  const [error, setError] = useState<string | null>(null)
  const actualizar = useActualizarHogar()

  const nombreValido = nombre.trim().length >= 2

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      await actualizar.mutateAsync({
        name: nombre.trim(),
        countExpectedIncome: contar,
        periodAnchorDay: ancla,
      })
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos guardar las preferencias.'))
    }
  }

  return (
    <FormSheet
      titulo="Preferencias del hogar"
      onCerrar={onCerrar}
      accion={
        <Guardar
          onClick={() => void enviar()}
          disabled={!nombreValido}
          guardando={actualizar.isPending}
        />
      }
    >
      <AvisoError mensaje={error} />

      <Campo
        label="Nombre del hogar"
        ayuda="Es lo que ven todos los integrantes en la parte de arriba."
        error={nombreValido ? undefined : 'Al menos 2 caracteres.'}
      >
        <Texto value={nombre} onChange={setNombre} maxLength={80} invalido={!nombreValido} />
      </Campo>

      <Interruptor
        label="Anticipar el ingreso que aún no llega"
        ayuda="El disponible siempre cuenta solo el dinero que ya está en tus cuentas. Con esto activo, además te decimos cuánto falta por caer y a cuánto llegarías, en su propia línea. Apágalo si prefieres no ver nada de lo que todavía no existe."
        value={contar}
        onChange={setContar}
      />

      {/* El periodo del hogar, configurable.
          Era el mes natural y nada más. Para quien cobra a mediados eso parte su
          nómina en dos ventanas: el disponible se derrumba cada día 1 sin que
          haya pasado nada, y los fijos del periodo salen a caballo entre dos
          meses. Es la ventana sobre la que se mide todo el dinero de la app, así
          que tiene que poder decir el mes que de verdad se vive. */}
      <Campo
        label="Cuándo empieza tu periodo"
        ayuda={
          ancla === 1
            ? 'El mes natural, del 1 al último día. Si cobras a mediados, elige el día que cobras: tu nómina dejará de partirse en dos periodos.'
            : `Tu periodo va del ${ancla} al ${ancla === 2 ? 1 : ancla - 1} del mes siguiente. Sobre esa ventana se calculan el disponible, los pagos fijos y los informes.`
        }
      >
        <DiaDelMes value={ancla} onChange={setAncla} max={MAX_PERIOD_ANCHOR_DAY} conUltimo={false} />
      </Campo>
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Integrantes
// ═══════════════════════════════════════════════════════════════════════════

const ROLES: ReadonlyArray<OpcionSegmento<MemberRole>> = [
  { value: 'ADMIN', label: 'Administra', detalle: 'Todo' },
  { value: 'MEMBER', label: 'Integrante', detalle: 'Ve el hogar' },
  { value: 'LIMITED', label: 'Limitado', detalle: 'Solo lo suyo' },
]

function IntegranteForm({
  miembro,
  integrantes,
  esPersonal,
  onCerrar,
}: {
  miembro: Member
  integrantes: readonly Member[]
  esPersonal: boolean
  onCerrar: () => void
}) {
  const [rol, setRol] = useState<MemberRole>(miembro.role)
  const [color, setColor] = useState<string>(miembro.personColorToken)
  const [aportacion, setAportacion] = useState(miembro.fixedContributionCents)
  const [error, setError] = useState<string | null>(null)
  const actualizar = useActualizarIntegrante()

  // Solo se ofrecen los colores libres. La base los fuerza únicos dentro del
  // hogar —dos personas del mismo color volverían ilegible cualquier gráfica—,
  // así que enseñar los ocupados solo llevaría a un error evitable.
  const ocupados = new Set(
    integrantes.filter((m) => m.id !== miembro.id).map((m) => m.personColorToken),
  )
  const opcionesColor: ReadonlyArray<OpcionSegmento<string>> = PERSON_TOKENS.filter(
    (t) => !ocupados.has(t),
  ).map((t) => ({ value: t, label: personPalette(t).label }))

  const enviar = async (): Promise<void> => {
    setError(null)
    try {
      await actualizar.mutateAsync({
        memberId: miembro.id,
        role: rol,
        personColorToken: color,
        ...(esPersonal ? {} : { fixedContributionCents: aportacion }),
      })
      haptic('success')
      onCerrar()
    } catch (cause) {
      haptic('error')
      setError(mensajeDeError(cause, 'No pudimos guardar los cambios.'))
    }
  }

  return (
    <FormSheet
      titulo={miembro.user.displayName}
      onCerrar={onCerrar}
      accion={<Guardar onClick={() => void enviar()} guardando={actualizar.isPending} />}
    >
      <AvisoError mensaje={error} />

      <Campo
        label="Rol en el hogar"
        ayuda="Administra: invita gente y cambia los ajustes. Integrante: ve y registra todo el hogar. Limitado: solo ve y registra su propio dinero — es el rol pensado para los hijos."
      >
        <Segmentos columnas={3} opciones={ROLES} value={rol} onChange={setRol} />
      </Campo>

      <Campo
        label="Color de identidad"
        ayuda="Con él se reconoce de quién es cada gasto sin leer nombres. Solo aparecen los colores libres: dentro de un hogar no se repiten."
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <PersonAvatar name={miembro.user.displayName} token={color} size="lg" />
            <p className="text-[13px] text-ink-soft">Así se verá en listas y gráficas.</p>
          </div>
          <Segmentos opciones={opcionesColor} value={color} onChange={setColor} />
        </div>
      </Campo>

      {!esPersonal ? (
        <Campo
          label="Aportación al fondo común"
          ayuda="Monto fijo al mes para los gastos compartidos."
        >
          <Monto cents={aportacion} onChange={setAportacion} />
        </Campo>
      ) : null}
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas compartidas
// ═══════════════════════════════════════════════════════════════════════════

function BotonSecundario({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-card border border-line bg-surface text-[14px] font-semibold text-ink active:scale-[0.98]"
    >
      {children}
    </button>
  )
}

/**
 * El mensaje del servidor manda sobre el genérico.
 *
 * La API contesta en español y explicando qué corregir —«Ese color ya
 * identifica a otro integrante», «El saldo de los vales no puede ser
 * negativo»—; sustituirlo por un «algo salió mal» tiraría justo la parte útil.
 */
function mensajeDeError(cause: unknown, respaldo: string): string {
  if (cause instanceof ApiError) return cause.message
  return respaldo
}

/**
 * Activación de avisos.
 *
 * El estado manda sobre lo que se ofrece. Pedir permiso a alguien cuyo navegador
 * nunca va a entregar el aviso —Safari en una pestaña, sin instalar— produce un
 * «sí» que no sirve para nada y deja al usuario creyendo que quedó activado.
 */
function AvisosPush() {
  const [estado, setEstado] = useState<EstadoPush>(() => estadoPush())
  const [activando, setActivando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activar = async (): Promise<void> => {
    setActivando(true)
    setError(null)
    try {
      // El permiso se pide desde este clic a propósito: pedirlo al cargar hace
      // que el navegador lo rechace de plano.
      const resultado = await activarPush()
      setEstado(resultado)
      haptic(resultado === 'activo' ? 'success' : 'warning')
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : (cause as Error).message)
    } finally {
      setActivando(false)
    }
  }

  if (estado === 'activo') {
    return (
      <Card className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-income-soft text-income-text">
          <CheckCircleIcon size={20} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-ink">Avisos activados</p>
          <p className="text-[12px] leading-snug text-ink-soft">
            Te avisamos el día de pago y antes de que venza cada tarjeta.
          </p>
        </div>
      </Card>
    )
  }

  if (estado === 'requiere-instalacion') {
    return (
      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-warning-soft text-warning">
            <DeviceMobileIcon size={20} weight="fill" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-ink">Instala PayBae para recibir avisos</p>
            <p className="text-[12px] leading-snug text-ink-soft">
              En iPhone, los avisos solo llegan si la app está en tu pantalla de inicio.
              No es un ajuste de PayBae: es como funciona Safari.
            </p>
          </div>
        </div>
        <ol className="flex flex-col gap-1.5 rounded-card bg-surface-sunken px-3.5 py-3 text-[13px] text-ink-soft">
          <li>1. Toca el botón <strong className="text-ink">Compartir</strong> de Safari.</li>
          <li>2. Elige <strong className="text-ink">Añadir a pantalla de inicio</strong>.</li>
          <li>3. Abre PayBae desde ahí y vuelve a esta pantalla.</li>
        </ol>
      </Card>
    )
  }

  if (estado === 'denegado') {
    return (
      <Card className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-faint">
          <BellSlashIcon size={20} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-ink">Avisos bloqueados</p>
          <p className="text-[12px] leading-snug text-ink-soft">
            Los bloqueaste antes. Para reactivarlos hay que hacerlo desde los ajustes del
            navegador; desde aquí ya no podemos volver a preguntarte.
          </p>
        </div>
      </Card>
    )
  }

  if (estado === 'no-soportado') return null

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
          <BellIcon size={20} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-ink">Activa los avisos</p>
          <p className="text-[12px] leading-snug text-ink-soft">
            Te avisamos el día que te pagan y antes de que venza cada tarjeta.
          </p>
        </div>
      </div>
      <Pastilla onClick={() => void activar()} ocupado={activando}>
        Activar avisos
      </Pastilla>
      {error ? (
        <p role="alert" className="text-[12px] text-expense-text">
          {error}
        </p>
      ) : null}
    </Card>
  )
}

/**
 * Invitación por link.
 *
 * Al crear la invitación, quien invita queda como administrador del hogar y el
 * nivel sube solo: personal pasa a pareja, y pareja a familia con el tercero.
 */
/**
 * Cambiar de hogar, en Ajustes.
 *
 * Solo aparece si hay más de uno, que es el caso de quien ya usaba PayBae por
 * su cuenta y después entró al de su pareja. Sin esto, moverse entre ellos solo
 * era posible aceptando otra invitación: el respaldo existía desde que el hogar
 * activo se guarda en el servidor, pero no había por dónde pedirlo.
 */
function CambiarHogar() {
  const { profile } = useSession()
  if (profile.memberships.length < 2) return null

  return (
    <section>
      <SectionHeader title="Tus hogares" />
      <Card className="flex flex-col gap-1.5">
        <ListaHogares />
      </Card>
    </section>
  )
}

function Invitar({ destacar = false }: { destacar?: boolean }) {
  const seccion = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!destacar) return

    // Salto instantáneo y en dos intentos.
    //
    // Instantáneo porque un desplazamiento suave de dos mil píxeles se queda a
    // medias en cuanto algo lo interrumpe, y quien pulsó «Invitar» acaba
    // mirando la pantalla de arriba sin saber por qué. Aterrizar de golpe donde
    // se pidió es justo lo que significa un atajo.
    //
    // Y en dos intentos porque las secciones de arriba —cuentas, ingresos,
    // integrantes— traen cada una su consulta y pasan de esqueleto a contenido
    // en momentos distintos: con un solo salto al montar, el destino todavía no
    // estaba donde acabaría.
    const ir = (): void =>
      seccion.current?.scrollIntoView({ behavior: 'auto', block: 'start' })

    const cuadro = requestAnimationFrame(ir)
    const asentado = window.setTimeout(ir, 450)
    return () => {
      cancelAnimationFrame(cuadro)
      window.clearTimeout(asentado)
    }
  }, [destacar])

  return (
    <section ref={seccion} className="scroll-mt-4">
      <SectionHeader title="Invitar a alguien" />
      <Card
        className={cn(
          'flex flex-col gap-3',
          // El realce dura lo que tarda la vista en encontrar el bloque y se
          // apaga solo: un resaltado permanente deja de señalar y pasa a ser
          // simplemente un recuadro más de color.
          destacar && 'animate-[realce_2.2s_ease-out_1]',
        )}
      >
        <Invitacion />
      </Card>
    </section>
  )
}
